import React, { memo, useRef, useState, useEffect } from 'react';
import { withStyles } from '@mui/styles';
import { Paper, IconButton, TextField } from '@mui/material';
import TrashIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useDrag, useDrop } from 'react-dnd';
import { trackItemType, sessionItemType } from './constants';
import SessionTypeChip from './SessionTypeChip';
import globalStyles from './styles';

export const TRACK_ITEM_WIDTH = 330;

const styles = {
  item: {
    ...globalStyles.itemWrapper,
    width: TRACK_ITEM_WIDTH,
    height: 'auto',
    minHeight: 50,
  },
  paper: {
    ...globalStyles.paper,
    height: 'auto',
    minHeight: 50,
    padding: '4px 4px',
    gap: 4,
  },
  handle: {
    cursor: 'move',
    color: '#999',
    display: 'flex',
    alignItems: 'center',
  },
  position: {
    width: 52,
    flexShrink: 0,
    '& .MuiOutlinedInput-input': {
      textAlign: 'center',
      padding: '6px 4px',
      fontSize: 14,
    },
  },
  name: {
    flexGrow: 1,
    minWidth: 0,
  },
  nameInput: {
    fontSize: 14,
    lineHeight: 1.3,
  },
  arrows: {
    display: 'flex',
    flexDirection: 'column',
  },
  arrowButton: {
    padding: 0,
  },
  iconButton: globalStyles.iconButton,
};

const TrackItem = ({
  item,
  trackId,
  sessionCount,
  handleMove,
  handleMoveToPosition,
  handleRenameSession,
  increaseNextTrackSessionId,
  handleDeleteTrackSession,
  classes,
}) => {
  const ref = useRef(null);
  const handleRef = useRef(null);
  const [positionDraft, setPositionDraft] = useState(String(item.order));

  useEffect(() => {
    setPositionDraft(String(item.order));
  }, [item.order]);

  const [{ draggingId }, drag, preview] = useDrag(
    () => ({
      type: trackItemType,
      collect: (monitor) => ({
        draggingId: monitor.getItem()?.id,
        isTrackItem: monitor.getItemType() === trackItemType,
      }),
      item: { ...item, trackId },
    }),
    [item, trackId],
  );
  const [, drop] = useDrop(
    () => ({
      accept: [trackItemType, sessionItemType],
      hover(dragItem) {
        if (!ref?.current) {
          return;
        }
        if (dragItem.id === item.id) {
          return;
        }

        handleMove({ dragItem, targetItem: { ...item, trackId } });
        dragItem.order = item.order;
        dragItem.trackId = trackId;
      },
      drop(dragItem, monitor) {
        if (monitor.getItemType() === sessionItemType) {
          increaseNextTrackSessionId();
        }
      },
    }),
    [item, trackId, increaseNextTrackSessionId],
  );

  drop(preview(ref));
  drag(handleRef);

  const commitPosition = () => {
    const position = parseInt(positionDraft, 10);
    if (Number.isNaN(position) || position === item.order) {
      setPositionDraft(String(item.order));
      return;
    }
    handleMoveToPosition({ trackId, id: item.id, position });
  };

  const opacity = draggingId === item.id ? 0.4 : 1;

  return (
    <div ref={ref} className={classes.item} style={{ opacity }}>
      <Paper elevation={1} className={classes.paper}>
        <span ref={handleRef} className={classes.handle} title="Drag to reorder">
          <DragIndicatorIcon fontSize="small" />
        </span>
        <TextField
          className={classes.position}
          size="small"
          variant="outlined"
          title="Position in track (type a number and press Enter)"
          value={positionDraft}
          onChange={({ target: { value } }) => setPositionDraft(value.replace(/[^0-9]/g, ''))}
          onBlur={commitPosition}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.target.blur();
            }
          }}
          inputProps={{ inputMode: 'numeric' }}
        />
        <TextField
          className={classes.name}
          size="small"
          variant="standard"
          multiline
          maxRows={4}
          placeholder="Session name"
          value={item.name ?? ''}
          error={!(item.name ?? '').trim()}
          onChange={({ target: { value } }) =>
            handleRenameSession({ sessionId: item.sessionId, value })
          }
          InputProps={{ className: classes.nameInput }}
        />
        <SessionTypeChip session={item} />
        <span className={classes.arrows}>
          <IconButton
            size="small"
            className={classes.arrowButton}
            title="Move up"
            disabled={item.order <= 1}
            onClick={() => handleMoveToPosition({ trackId, id: item.id, position: item.order - 1 })}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            className={classes.arrowButton}
            title="Move down"
            disabled={item.order >= sessionCount}
            onClick={() => handleMoveToPosition({ trackId, id: item.id, position: item.order + 1 })}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </span>
        <IconButton
          className={classes.iconButton}
          color="primary"
          onClick={() => handleDeleteTrackSession({ id: item.id, trackId })}
        >
          <TrashIcon />
        </IconButton>
      </Paper>
    </div>
  );
};

export default memo(withStyles(styles)(TrackItem));
